import React from "react";

const rollingRateExplanation = (
  <div>
    <h2>Rolling Agreed and Recovered Rates Comparison</h2>

    <h3>What are these rates?</h3>
    <p>
      <strong>Agreed Rate:</strong> The average agreed hourly rate.
    </p>
    <p>
      <strong>Recovered Rate:</strong> The average hourly rate actually realized after billing and
      collection.
    </p>

    <h3>How are they calculated?</h3>
    <p>For each time period in the 12-month rolling window:</p>
    <p>Agreed Rate = Average Charge Rate / Average Time Recorded</p>
    <p>Recovered Rate = Average Amount Collecte / Average Time Recorded</p>
    <p>Where:</p>
    <ul>
      <li>
        <strong>Average Charge Rate</strong> is the mean hourly rate agreed at the outset of a
        matter.
      </li>
      <li>
        <strong>Average Amount Collected</strong> comes from the actual payments received on
        invoices
      </li>
      <li>
        <strong>Average Time Recorded</strong> is the mean time recorded for work on matters
      </li>
    </ul>

    <h3>What do these graphs show?</h3>
    <p>
      These graphs compare the Agreed and Recovered Rates for matters handled with VPD (blue lines)
      to those without VPD (red lines) over time. Each point represents a 12-month moving average.
    </p>

    <h3>Why are these important?</h3>
    <p>
      <strong>Agreed Rate:</strong> Reflects the average hourly rate fee earners are recording for
      their work.
    </p>
    <p>
      <strong>Recovered Rate:</strong> Shows what the firm actually collects per hour of recorded
      time.
    </p>
    <p>The difference can indicate discounts, write-offs, or collection issues.</p>

    <h3>Key Aspects to Consider:</h3>
    <ul>
      <li>Compare VPD and non-VPD lines for both rates.</li>
      <li>Note the gap between Agreed and Recovered Rates for each category.</li>
      <li>Observe trends in these rates over time.</li>
    </ul>

    <h3>What could this mean for your firm?</h3>
    <p>These graphs can help you assess:</p>
    <ul>
      <li>How VPD might influence the rates fee earners record and the firm collects.</li>
      <li>
        Whether VPD-managed matters show better alignment between recorded and collected rates.
      </li>
      <li>Potential areas for improving your billing and collection processes.</li>
    </ul>

    <p>
      Note: These rates are calculated averages based on time entries and collections. They may not
      reflect specific rates for individual matters or fee earners.
    </p>
  </div>
);

const writeOffExplanation = (
  <div>
    <h2>Write-Off Percentage Comparison</h2>

    <h3>What is Write-Off Percentage?</h3>
    <p>
      Write-Off Percentage represents the portion of the agreed charges that the firm doesn&apos;t
      collect, expressed as a percentage of the total agreed charges.
    </p>

    <h3>How is it calculated?</h3>
    <p>For each matter in the 12-month rolling window:</p>
    <p>Write-Off Percentage = (Agreed Charges - Recovered Amount) / Agreed Charges × 100%</p>
    <p>Where:</p>
    <ul>
      <li>
        <strong>Agreed Charges</strong> is the total amount based on fee earners&apos; recorded time
        and charge rates
      </li>
      <li>
        <strong>Recovered Amount</strong> is the actual payment received from clients
      </li>
    </ul>

    <h3>What does this graph show?</h3>
    <p>
      This graph compares the average Write-Off Percentage for matters handled with VPD (blue line)
      to those without VPD (red line) over time. Each point represents a 12-month moving average.
    </p>

    <h3>Why is this important?</h3>
    <p>
      It quantifies the gap between what fee earners record and what the firm actually collects.
    </p>
    <p>Lower write-off percentages generally indicate better financial performance.</p>
    <p>It can reveal the effectiveness of pricing, billing, and collection practices.</p>

    <h3>Key Aspects to Consider:</h3>
    <ul>
      <li>Compare the VPD and non-VPD lines.</li>
      <li>Look for consistent patterns or differences between the two lines over time.</li>
      <li>Note any significant changes or trends in either line.</li>
    </ul>

    <h3>What could this mean for your firm?</h3>
    <p>This graph can help you assess:</p>
    <ul>
      <li>How VPD might impact your firm&apos;s ability to realize recorded charges.</li>
      <li>Whether VPD-managed matters experience lower write-offs.</li>
      <li>Potential for improving overall financial performance by reducing write-offs.</li>
    </ul>

    <p>
      Note: Write-offs can occur for various reasons, including strategic decisions, client
      negotiations, or inefficiencies. This metric provides an overview, but individual matter
      context is important for a full understanding.
    </p>
  </div>
);

const chartExplanations: { [key: string]: string[] | React.ReactNode } = {
  cumulativeVpdProfit: (
    <div>
      <h2>Cumulative Additional Profit</h2>

      <h3>What does this graph show?</h3>
      <p>
        This graph estimates the extra profit earned on VPD matters over time, compared to if those
        matters had been handled without VPD.
      </p>

      <h3>How is it calculated?</h3>
      <p>For each time period, we follow these steps:</p>
      <ol>
        <li>
          Calculate the profitability improvement factor:
          <ul>
            <li>Improvement Factor = VPD Gross Profit Margin / Non-VPD Gross Profit Margin</li>
          </ul>
        </li>
        <li>
          For each VPD matter, estimate the additional profit:
          <ul>
            <li>Additional Profit = Actual VPD Profit - Estimated Non-VPD Profit</li>
          </ul>
        </li>
        <li>
          Where:
          <ul>
            <li>
              <strong>Actual VPD Profit</strong> = VPD Revenue - VPD Costs
            </li>
            <li>
              <strong>Estimated Non-VPD Profit</strong> = VPD Revenue * (Non-VPD Profit Margin)
            </li>
          </ul>
        </li>
        <li>Sum the Additional Profit for all VPD matters in the period.</li>
        <li>Add this sum to the running total from previous periods.</li>
      </ol>
      <p>Each point on the graph represents this cumulative total at that time.</p>

      <h3>Why is this important?</h3>
      <p>It quantifies the estimated financial impact of using VPD over time.</p>
      <p>It shows how this impact accumulates or changes across different periods.</p>
      <p>It helps assess the ongoing value VPD brings to your firm.</p>

      <h3>Key Aspects to Consider:</h3>
      <ul>
        <li>The slope of the curve indicates the rate of additional profit accumulation.</li>
        <li>
          Flat or downward sections might suggest periods where VPD and non-VPD performance was
          similar.
        </li>
        <li>
          Consider how this additional profit compares to your firm&apos;s overall profitability.
        </li>
      </ul>

      <h3>Potential Profit Number</h3>
      <p>
        The &ldquo;Potential Profit&rdquo; number at the top of the page uses a similar calculation,
        but for non-VPD matters:
      </p>
      <p>
        Potential Profit = Sum of (Estimated VPD Profit - Actual Non-VPD Profit) for all non-VPD
        matters
      </p>
      <p>Where:</p>
      <ul>
        <li>
          <strong>Estimated VPD Profit</strong> = Non-VPD Revenue * (VPD Profit Margin)
        </li>
        <li>
          <strong>Actual Non-VPD Profit</strong> = Non-VPD Revenue - Non-VPD Costs
        </li>
      </ul>
      <p>This is calculated for all non-VPD matters and summed over the selected timeframe.</p>

      <h3>What could this mean for your firm?</h3>
      <p>These calculations can help you:</p>
      <ul>
        <li>Quantify the current financial impact of VPD on your matters.</li>
        <li>Estimate the potential benefit of expanding VPD use.</li>
        <li>Identify periods of higher or lower VPD effectiveness for further investigation.</li>
      </ul>

      <p>
        <strong>Important Note:</strong> While based on your firm&apos;s data, these calculations
        involve modelling and assumptions. They are best used as indicators of potential rather than
        guaranteed outcomes. Actual results may vary based on many factors specific to your firm and
        matters.
      </p>
    </div>
  ),
  mattersCreated: (
    <div>
      <h2>Matters Created</h2>

      <h3>What does this graph show?</h3>
      <p>
        This graph displays the number of new matters created each month, distinguishing between all
        matters and those managed using VPD.
      </p>

      <h3>How is it calculated?</h3>
      <p>
        We simply count the number of new matters opened in each month, separating VPD matters from
        the total.
      </p>

      <h3>Why is this important?</h3>
      <p>It shows the overall volume of new business your firm is handling.</p>
      <p>It illustrates the adoption rate of VPD for new matters over time.</p>

      <h3>Key Aspects to Consider:</h3>
      <ul>
        <li>The total height of each bar represents all new matters for that month.</li>
        <li>The teal portion of each bar shows how many of those new matters used VPD.</li>
        <li>Look for trends in both overall matter volume and VPD adoption.</li>
      </ul>

      <h3>What could this mean for your firm?</h3>
      <p>This graph can help you:</p>
      <ul>
        <li>Track the growth of your firm&apos;s new business over time.</li>
        <li>Monitor the progress of VPD implementation across new matters.</li>
        <li>Identify periods of increased or decreased VPD adoption for further analysis.</li>
        <li>Set goals for expanding VPD use in future matters.</li>
      </ul>

      <p>
        Remember, this graph shows new matters only and doesn&apos;t reflect the total number of
        active matters your firm is handling at any given time.
      </p>
    </div>
  ),
  grossProfitMargin: (
    <div>
      <h2>Gross Profit Margin (GPM) Comparison</h2>

      <h3>What is GPM?</h3>
      <p>
        Gross Profit Margin is a key indicator of profitability. It shows what percentage of revenue
        is retained after accounting for the direct costs of providing legal services.
      </p>

      <h3>How is it calculated?</h3>
      <p>For each matter, we calculate:</p>
      <p>GPM = (Revenue - Direct Costs) / Revenue × 100%</p>
      <p>Where:</p>
      <ul>
        <li>
          <strong>Revenue</strong> is the amount billed to the client
        </li>
        <li>
          <strong>Direct Costs</strong> include fee earner time and other direct expenses
        </li>
      </ul>

      <h3>What does this graph show?</h3>
      <p>
        This graph compares the average GPM for matters handled with VPD (blue line) to those
        without VPD (red line) over time. Each point represents a 12-month moving average, helping
        to smooth out short-term fluctuations and show overall trends.
      </p>

      <h3>Why is this important?</h3>
      <p>
        A higher GPM indicates greater profitability. The difference between the VPD and non-VPD
        lines demonstrates the potential financial benefit of using VPD for matter pricing and
        management.
      </p>

      <h3>Key Aspects to Consider:</h3>
      <ul>
        <li>Compare the relative positions of the VPD and non-VPD lines.</li>
        <li>Look for consistent patterns or differences between the two lines over time.</li>
        <li>Note any significant changes or trends in either line.</li>
      </ul>

      <h3>What could this mean for your firm?</h3>
      <p>
        By examining how VPD impacts GPM in this data, you can assess the potential value of
        adopting VPD more widely in your firm. The tool&apos;s pricing guidance and matter
        management features could help you optimize your GPM across more matters.
      </p>
      <p>
        Remember, this graph represents aggregated data and trends may vary based on specific firm
        characteristics and matter types.
      </p>
    </div>
  ),
  rollingAgreedRate: rollingRateExplanation,
  rollingRecoveredRate: rollingRateExplanation,
  writeOff: writeOffExplanation,
  writeOffPercentage: writeOffExplanation,
  averageTotalCharge: (
    <div>
      <h2>Total Charge Per Matter</h2>

      <h3>What does this graph show?</h3>
      <p>
        This graph displays the average total charge per matter over time, comparing VPD matters
        (blue line) to non-VPD matters (red line).
      </p>

      <h3>How is it calculated?</h3>
      <p>For each month, we:</p>
      <ol>
        <li>
          Sum up the total charges for all VPD matters and divide by the number of VPD matters.
        </li>
        <li>Do the same for non-VPD matters.</li>
      </ol>

      <h3>Why is this important?</h3>
      <p>It shows the average revenue generated per matter for both VPD and non-VPD matters.</p>
      <p>It helps compare the financial performance of VPD matters against non-VPD matters.</p>

      <h3>Key Aspects to Consider:</h3>
      <ul>
        <li>
          The consistent gap between VPD and non-VPD lines suggests a significant difference in
          average charges.
        </li>
        <li>VPD matters show higher average charges throughout the period.</li>
        <li>
          Both lines show some fluctuation, which could reflect changes in matter complexity or
          pricing strategies.
        </li>
      </ul>

      <h3>What could this mean for your firm?</h3>
      <p>This graph can help you:</p>
      <ul>
        <li>Quantify the potential revenue impact of using VPD for matter management.</li>
        <li>Identify trends in matter pricing and value over time.</li>
        <li>
          Support discussions about pricing strategies and the financial benefits of VPD adoption.
        </li>
        <li>
          Investigate periods of significant changes in average charges for potential insights.
        </li>
      </ul>

      <p>
        Remember, while higher charges are generally positive, it&apos;s important to consider this
        data alongside other metrics like profitability and client satisfaction for a complete
        picture.
      </p>
    </div>
  ),
};

export default chartExplanations;
